// import library
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { getApiKey } from "../../services/userCompany";
import { AuthContext } from "../../context";

// import component
import Layout from "../../layouts/Layout";
import LoaderIcon from "../../components/Common/Icon/Loader";

// import files
import { env } from "../../env";
import MapComponent from "../../components/Dashboard/MapComponent";
import CameraCaptureComponent from "../../components/Profile/CameraCaptureComponent";
import WarningPopUp from "../../components/Profile/WarningPopUp";

const ProfileEdit = () => {
    // variables
    const { t } = useTranslation();
    const [isLoader, setIsLoader] = useState(true);
    const authContext = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const customerid = location.pathname.split('/')[3];
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const storedVisitData = localStorage.getItem('visit');
    const initialVisitData = storedVisitData ? JSON.parse(storedVisitData) : {};
    const [visitTypeData, setVisitTypeData] = useState(initialVisitData);
    const [showWarningPopUp, setShowWarningPopUp] = useState(false);

    const [showPartner, setShowPartner] = useState(false);
    const [showBusinessUnit, setShowBusinessUnit] = useState(false);
    const [showCategory, setShowCategory] = useState(false);
    const [formButton, setFormButton] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [showChooseOption, setShowChooseOption] = useState(false);
    const [showOpenCamera, setShowOpenCamera] = useState(false);

    const [customerData, setCustomerData] = useState({});
    const [partnerList, setPartnerList] = useState([]);
    const [businessUnitList, setBusinessUnitList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [file, setFile] = useState("");
    const [fileName, setFileName] = useState("");
    const [fileSize, setFileSize] = useState(0);
    const [error, setError] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [markers, setMarkers] = useState([]);
    const [inputs, setInputs] = useState({});

    const [selectedPartners, setSelectedPartners] = useState([]);
    const [defaultPartner, setDefaultPartner] = useState(null);
    const [businessData, setBusinessData] = useState({
        businessId: 0,
        businessName: "",
    });
    const [categoryData, setCategoryData] = useState({
        categoryId: 0,
        categoryName: ""
    });

    const apiRequest = async (method, url, data = null) => {
        try {
            const response = await axios({
                method,
                url,
                data,
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error(error);
            if (error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem('token');
                localStorage.removeItem('userInfo');
                window.location.reload();
            }
            throw error; 
        }
    };

    const apiRequestTwo = async (method, url, data = null) => {
        try {
            const response = await axios({
                method,
                url,
                data,
                headers: {
                    "Content-Type" : "multipart/form-data",
                    "Authorization" : `Bearer ${token}`
                },
            });
            return response.data;
        } catch (error) {
            console.error(error);
            if (error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem('token');
                localStorage.removeItem('userInfo');
                window.location.reload();
            }
            throw error; 
        }
    };

    const fetchData = async () => {
        try {
            // Step 1: Get Partner List
            const partnerData = await apiRequest('get', `${baseUrl}/api/OfflineSync/GetPartnerList`);
            setPartnerList(partnerData);

            // Step 2: Get Business Unit List
            const businessUnitData = await apiRequest('get', `${baseUrl}/api/OfflineSync/GetAllBusinessUnitList`);
            setBusinessUnitList(businessUnitData);

            // Step 3: Get Category List
            const categoryData = await apiRequest('get', `${baseUrl}/api/OfflineSync/GetAllCustomerCategoriesList`);
            setCategoryList(categoryData);

            // Step 4: Get Customer Details
            const customerDataResponse = await apiRequestTwo('post', `${baseUrl}/api/Customer/GetCustomerDetail`, { customerid });
            const customer = customerDataResponse[0];

            // Process customer data
            processCustomerData(businessUnitData, categoryData, customer);
            setCustomerData(customer);
            setIsLoader(false);

        } catch (error) {
            setIsLoader(false);
            console.error(error);
        }
    };

    const processCustomerData = (businessUnitData, categoryData, customer) => {
        // Process Partner Data
        if (customer.partner) {
            const initialCustomerPartnerData = JSON.parse(customer.partner);
            const selectedIds = initialCustomerPartnerData.map((partner) => partner.PartnerID);
            const defaultPartnerId = initialCustomerPartnerData.find((partner) => partner.IsDefault)?.PartnerID;
            setSelectedPartners(selectedIds);
            setDefaultPartner(defaultPartnerId);
        }

        // Process Business Unit Data
        if (customer.businessUnit) {
            const business = businessUnitData.find(business => business.name === customer.businessUnit);
            setBusinessData({
                businessId: business ? business.id : 0,
                businessName: business ? business.name : "",
            });
        }

        // Process Category Data
        if (customer.category) {
            const category = categoryData.find(category => category.categoriesname === customer.category);
            setCategoryData({
                categoryId: category ? category.categoriesid : "",
                categoryName: category ? category.categoriesname : "",
            });
        }

        // Set form inputs and map markers
        setInputs({
        ...inputs,
            name: customer.name || "",
            postalCode: customer.postalCode || "",
            address: customer.address || "",
            state: customer.state || "",
            city: customer.city || "",
            country: customer.country || "",
            map: customer.addressLatitude ? `${customer.addressLatitude}, ${customer.addressLongitude}` : "",
            addressLatitude: customer.addressLatitude || "",
            addressLongitude: customer.addressLongitude || "",
            customerTitle: customer.customerTitle || "",
            contactPerson: customer.contactName || "",
            phone: (customer.phone && customer.phone !== "NA") ? customer.phone : "",
            mobileNumber: customer.mobileNumber || "",
            secondaryMail: customer.secondaryEmail || "",
            email: customer.email || "",
            code: customer.code || ""
        });

        if (customer.addressLatitude !== 'NULL' && customer.addressLongitude !== 'NULL') {
        setMarkers([{
            ...markers,
                geocode: [customer.addressLatitude, customer.addressLongitude],
                    popUpData: {
                    lastName: customer.customerTitle ? customer.customerTitle : "",
                    name: customer.contactName ? customer.contactName : "",
                    address: customer.address || "",
                    phone: (customer.phone && customer.phone !== "NA") ? customer.phone : "",
                    mobileNumber: customer.mobileNumber || "",
                    date : customer.lastVisitedDate ? customer.lastVisitedDate : ""
                }
            }]);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // tab change
    const handleTabCheckDataFromChild = (data) => {
        if((visitTypeData && visitTypeData.visitId) && data.tab === 'profile') {
            setShowWarningPopUp(!showWarningPopUp)
        } else {
            if(data.link === '/logout') {
                authContext.logout();
            } else {
                navigate(data.link);
            }
            localStorage.removeItem("profile_tab");
        }
    };
    const handleWarningPopUpDataFromChild = (data) => {
        setShowWarningPopUp(!showWarningPopUp);
        if(data === 'complete') {
            setVisitTypeData({});
        }
    };
    // functions
    const handleImageChange = (e) => {
        setError("");
        const selectedFile = e.target.files[0];
        
        if (selectedFile) {
            const allowedTypes = ['image/png', 'image/jpeg'];
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            
            // Check file type
            if (!allowedTypes.includes(selectedFile.type)) {
                setError('Invalid file format. Allowed formats are .png, .jpg, .jpeg');
                setFile(null);
                setFileName('');
                setFileSize(0);
                setImagePreview(null);
                return;
            }

            // Check file size
            if (selectedFile.size > maxSize) {
                setError('File size exceeds 10MB');
                setFile(null);
                setFileName('');
                setFileSize(0);
                setImagePreview(null);
                return;
            }

            // Convert the file to Base64
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result.split(',')[1]; // Remove data URL prefix
                setFile(base64String);  // Store Base64 string
                setFileName(selectedFile.name);
                setFileSize(selectedFile.size);
                setImagePreview(reader.result);
            };

            reader.readAsDataURL(selectedFile);
        }
    };
    const handleCameraCloseDataFromChild = (data) => {
        setShowOpenCamera(data)
    };
    const handleImageDataFromChild = (data) => {
        setError("");
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result.split(',')[1];
            setFile(base64String);
            setFileName(data.name);
            setFileSize(data.size);
            setImagePreview(reader.result);
        };
        
        reader.readAsDataURL(data);
        setShowOpenCamera(false);
        setShowChooseOption(false);
    };
    const handleShowCategory = (e) => {
        e.preventDefault();
        setShowCategory(!showCategory);
        setShowBusinessUnit(false);
        setShowPartner(false);
    };
    const handleShowBusinessUnit = (e) => {
        e.preventDefault();
        setShowBusinessUnit(!showBusinessUnit);
        setShowCategory(false);
        setShowPartner(false);
    };
    const handleShowPartner = (e) => {
        e.preventDefault();
        setShowPartner(!showPartner);
        setShowBusinessUnit(false);
        setShowCategory(false);
    };
    const handleCategory = (e, value) => {
        e.preventDefault();
        setShowCategory(false);
        setCategoryData({
            ...categoryData,
            categoryId: value.categoriesid,
            categoryName: value.categoriesname,
        });
    };
    const handleBusinessUnit = (e, value) => {
        e.preventDefault();
        setShowBusinessUnit(false);
        setBusinessData({
            ...businessData,
            businessId: value.id,
            businessName: value.name,
        });
    };
    const handlePartnerChange = (id) => {
        setShowBusinessUnit(false);
        setShowCategory(false);
        setSelectedPartners((prevState) => {
            const updatedSelected = prevState.includes(id)
                ? prevState.filter((partnerId) => partnerId !== id)  // Deselecting the partner
                : [...prevState, id];  // Selecting the partner
    
            if (updatedSelected.length > 0 && !defaultPartner) {
                setDefaultPartner(updatedSelected[0]);
            } else if (updatedSelected.length === 0) {
                setDefaultPartner(null);
            } else {
                if (updatedSelected.length > 0 && updatedSelected[0] !== defaultPartner) {
                    setDefaultPartner(updatedSelected[0]);
                }
            }
    
            return updatedSelected;
        });
    };
    const handleChange = (e) => {
        setInputs({
            ...inputs,
            [e.target.name] : e.target.value
        });
    };
    const handleDefaultChange = (id) => {
        setDefaultPartner(id);
    };
    const prepareDataForRequest = () => {
        if (selectedPartners.length === 0) {
            return null; 
        }

        const requestData = {
            partner_ids: selectedPartners.map((partnerId) => {
                const partner = partnerList.find((p) => p.PartnerId === partnerId);
                return {
                    id: partner.PartnerId,
                    isdefault: partner.PartnerId === defaultPartner
                };
            })
        };

        return requestData;
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        const requestData = prepareDataForRequest();

        Swal.fire({
            title: `${ t("are you sure")}?`,
            text: `${ t("you want to update customer data")}!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `${ t("yes")}!`,
            cancelButtonText: `${ t("cancel")}!`
        }).then((result) => {
            if (result.isConfirmed) {
                setFormButton(true);
                const baseUrl = env.REACT_APP_API_URL;
            
                const headers = {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "APIkey": getApiKey(authContext.userInfo),
                    "Username": authContext.userInfo.userName,
                };
            
                axios.post(baseUrl+"/api/CustomerDetails/InsertSingleCustomer", {
                    id: customerid,
                    name: inputs.name,
                    email: inputs.email,
                    address: inputs.address,
                    phone: inputs.phone,
                    city: inputs.city,
                    state: inputs.state,
                    postalCode: inputs.postalCode,
                    code: inputs.code,
                    addressLatitude: inputs.addressLatitude,
                    addressLongitude: inputs.addressLongitude,
                    isActive: true,
                    showinPDMC: true,
                    contactname: inputs.contactPerson,
                    country: inputs.country,
                    customerTitle: inputs.customerTitle,
                    email2: inputs.secondaryMail,
                    mobileNumber: inputs.mobileNumber,
                    image: file,
                    partner_ids: requestData && requestData.partner_ids,
                    CategoryID: categoryData.categoryId,
                    BusinessUnitID: businessData.businessId
                }, { headers })
                .then((response) => {
                    if(response.data.statuscode === 200) {
                        setFormButton(false);
                        fetchData();
                        Swal.fire({
                            title: `${ t("updated")}!`,
                            text: `${ t("customer detail updated")}!`,
                            icon: "success"
                        });
                    }
                }).catch((error) => {
                    console.log(error);
                    setFormButton(false);
                    if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                        localStorage.removeItem("token");
                        localStorage.removeItem("userInfo");
                        window.location.reload();
                    } else {
                        Swal.fire({
                            title: `${ t("oops")}!`,
                            text: `${ t("something went wrong")}!`,
                            icon: "warning",
                        });
                    }
                });
            }
        });
    };
    
    return (
        <div className="relative">
            <Layout sendTabCheckDataFromChild={handleTabCheckDataFromChild}>
                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="space-y-2 sm:space-y-4">
                        {/* first box */}
                        <div className="flex justify-between items-center">
                            <div>
                                <h1 className="sm:text-2xl font-semibold capitalize">{ t('edit details') }</h1>
                                <p className="text-light-400 text-xs sm:text-sm capitalize"><NavLink to={`/profiles/detail/${customerid}`}>{ t('customer profile') }</NavLink>/{ t('edit profile') }</p>
                            </div>
                            <div className="hidden sm:flex justify-end items-center space-x-4">
                                {!formButton &&
                                    <NavLink to={`/profiles/detail/${customerid}`}>
                                        <button type="button" className="text-center px-2 sm:px-4 text-sm font-medium sm:text-base py-2 text-blue-normal bg-white border border-blue-normal rounded-lg tracking-wide capitalize">{ t('cancel') }</button>
                                    </NavLink>
                                }
                                <button type={formButton ? "button" : "submit"} className="text-center px-2 sm:px-4 text-sm font-medium sm:text-base py-2 text-white bg-blue-normal border border-blue-normal rounded-lg tracking-wide capitalize">{formButton ? t('Please wait, in-progress') : t('save') }</button>
                            </div>
                            <NavLink to={`/profiles/detail/${customerid}`} className="sm:hidden block">
                                <div className="bg-black-100 flex items-center p-3 rounded-lg">
                                    <img src="../../assets/icons/profile/cancel.svg" alt="cancel" />
                                </div>
                            </NavLink>
                        </div>
                        { isLoader
                        ?
                            <LoaderIcon />
                        :
                            <div className="space-y-2 sm:space-y-4">
                                <div className="bg-white border border-white rounded-lg py-2 sm:py-4">
                                    <div className="px-2 sm:px-4 flex items-center justify-between">
                                        <h1 className="text-base sm:text-lg font-semibold capitalize">{ t('address details')}</h1>
                                    </div>
                                    <div className="my-4 border-t border-background-normal"></div>
                                    <div className="hidden lg:block px-2 sm:px-4 space-y-0 lg:space-y-4">
                                        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 space-y-2 lg:space-y-0">
                                            <div className="space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('postal code')}
                                                </div>
                                                <input type="text" name="postalCode" value={inputs.postalCode} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("postal code")} />
                                            </div>
                                            <div className="space-y-2 lg:col-span-2">
                                                <div className="font-semibold capitalize">
                                                    { t('address')}
                                                </div>
                                                <input type="text" name="address" value={inputs.address} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("address")} />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('city')}
                                                </div>
                                                <input type="text" name="city" value={inputs.city} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("city")} />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('state')}
                                                </div>
                                                <input type="text" name="state" value={inputs.state} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("state")} />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('country')}
                                                </div>
                                                <input type="text" name="country" value={inputs.country} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("country")} />
                                            </div>
                                        </div>
                                        <div className="space-y-2 w-64">
                                            <div className="font-semibold capitalize">
                                                { t('view on map')}
                                            </div>
                                            <div className="border-background-normal bg-white border-2 rounded-lg px-4 py-2 flex justify-between">
                                                <div>{inputs.map}</div>
                                                <img onClick={(e) => (inputs.map ? setShowMap(true) : setShowMap(false))} className={inputs.map ? "cursor-pointer" : "cursor-not-allowed"} src="../../assets/icons/profile/location.svg" alt="location" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="block lg:hidden px-2 text-sm sm:text-sm space-y-4">
                                        <div className="grid grid-cols-3 gap-2">
                                            <div className="space-y-1">
                                                <div className="font-semibold capitalize">
                                                    { t('postal code')}
                                                </div>
                                                <input type="text" name="postalCode" value={inputs.postalCode} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg p-2 w-full placeholder:capitalize" placeholder={ t("postal code")} />
                                            </div>
                                            <div className="space-y-1 col-span-2">
                                                <div className="font-semibold capitalize">
                                                    { t('address')}
                                                </div>
                                                <input type="text" name="address" value={inputs.address} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg p-2 w-full placeholder:capitalize" placeholder={ t("address")} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <div className="font-semibold capitalize">
                                                    { t('city')}
                                                </div>
                                                <input type="text" name="city" value={inputs.city} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg p-2 w-full placeholder:capitalize" placeholder={ t("city")} />
                                            </div>
                                            <div className="space-y-1">
                                                <div className="font-semibold capitalize">
                                                    { t('state')}
                                                </div>
                                                <input type="text" name="state" value={inputs.state} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg p-2 w-full placeholder:capitalize" placeholder={ t("state")} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            <div className="space-y-1">
                                                <div className="font-semibold capitalize">
                                                    { t('country')}
                                                </div>
                                                <input type="text" name="country" value={inputs.country} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg p-2 w-full placeholder:capitalize" placeholder={ t("country")} />
                                            </div>
                                            <div className="space-y-1 col-span-2">
                                                <div className="font-semibold capitalize">
                                                    { t('view on map')}
                                                </div>
                                                <div className="border-background-normal bg-white border-2 rounded-lg p-2 flex justify-between">
                                                    <div>{inputs.map}</div>
                                                    <img onClick={(e) => (inputs.map ? setShowMap(true) : setShowMap(false))} className={inputs.map ? "cursor-pointer" : "cursor-not-allowed"} src="../../assets/icons/profile/location.svg" alt="location" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white border border-white rounded-lg py-2 sm:py-4">
                                    <div className="px-2 sm:px-4 flex items-center justify-between">
                                        <h1 className="text-base sm:text-lg font-semibold capitalize">{ t('communication details')}</h1>
                                    </div>
                                    <div className="my-4 border-t border-background-normal"></div>
                                    <div className="px-2 sm:px-4 hidden lg:grid grid-cols-4 gap-4">
                                        <div className="col-span-2 lg:col-span-3 grid grid-cols-1 lg:grid-cols-4 gap-4">
                                            <div className="space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('customer title')}
                                                </div>
                                                <input type="text" name="customerTitle" value={inputs.customerTitle} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("customer title")} />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('contact person')}
                                                </div>
                                                <input type="text" name="contactPerson" value={inputs.contactPerson} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("contact person")} />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('phone number')}
                                                </div>
                                                <input type="text" name="phone" value={inputs.phone} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("phone number")} />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('mobile number')}
                                                </div>
                                                <input type="text" name="mobileNumber" value={inputs.mobileNumber} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("mobile number")} />
                                            </div>
                                            <div className="lg:col-span-2 space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('secondary email address')}
                                                </div>
                                                <input type="text" name="secondaryMail" value={inputs.secondaryMail} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("secondary email address")} />
                                            </div>
                                            <div className="lg:col-span-2 space-y-2">
                                                <div className="font-semibold capitalize">
                                                    { t('email address')}
                                                </div>
                                                <input type="text" name="email" value={inputs.email} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("email address")} />
                                            </div>
                                        </div>
                                        <div className="col-span-2 lg:col-span-1 flex flex-col lg:justify-center items-center">
                                            <label htmlFor="dropzone-file1" className="border-dashed border border-gray-dark rounded-lg flex flex-col justify-center items-center w-40 h-40 cursor-pointer">
                                                <img className={(imagePreview ? "" : "w-6 h-6") + " cursor-pointer"} src={imagePreview ? imagePreview : "../../assets/icons/profile/camera.svg"} />
                                                {imagePreview ? "" : <div className="text-blue-normal capitalize font-semibold">{ t('add image')}</div>}
                                                <input id="dropzone-file1" type="file" className="hidden" onChange={handleImageChange} />
                                            </label>
                                            {error && <p className="text-red-normal text-xs font-semibold">{error}</p>}
                                        </div>
                                    </div>
                                    <div className="px-2 text-sm sm:text-sm space-y-4 block lg:hidden">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <div className="font-semibold capitalize">
                                                    { t('customer title')}
                                                </div>
                                                <input type="text" name="customerTitle" value={inputs.customerTitle} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("customer title")} />
                                            </div>
                                            <div className="space-y-1">
                                                <div className="font-semibold capitalize">
                                                    { t('contact person')}
                                                </div>
                                                <input type="text" name="contactPerson" value={inputs.contactPerson} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("contact person")} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <div className="font-semibold capitalize">
                                                    { t('phone number')}
                                                </div>
                                                <input type="text" name="phone" value={inputs.phone} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("phone number")} />
                                            </div>
                                            <div className="space-y-1">
                                                <div className="font-semibold capitalize">
                                                    { t('mobile number')}
                                                </div>
                                                <input type="text" name="mobileNumber" value={inputs.mobileNumber} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("mobile number")} />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="font-semibold capitalize">
                                                { t('secondary email address')}
                                            </div>
                                            <input type="text" name="secondaryMail" value={inputs.secondaryMail} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("secondary email address")} />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="font-semibold capitalize">
                                                { t('email address')}
                                            </div>
                                            <input type="text" name="email" value={inputs.email} onChange={(e) => handleChange(e)} className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full placeholder:capitalize" placeholder={ t("email address")} />
                                        </div>
                                        <div onClick={(e) => setShowChooseOption(!showChooseOption)} className="flex flex-col lg:justify-center items-center">
                                            <div className="border-dashed border border-gray-dark rounded-lg flex flex-col justify-center items-center w-40 h-40 cursor-pointer">
                                                <img className={(imagePreview ? "" : "w-6 h-6") + " cursor-pointer"} src={imagePreview ? imagePreview : "../../assets/icons/profile/camera.svg"} />
                                                {imagePreview ? "" : <div className="text-blue-normal capitalize font-semibold">{ t('add image')}</div>}
                                            </div>
                                        </div>
                                        {
                                            showChooseOption ?
                                                <div className="flex justify-between">
                                                    <button onClick={(e) => setShowOpenCamera(!showOpenCamera)} type="button" className="capitalize text-white bg-blue-normal border-blue-normal text-center px-4 text-xs font-mediu py-2 border rounded-lg tracking-wide">{t('take photo')}</button>
                                                    <label className="capitalize text-white bg-blue-normal border-blue-normal text-center px-4 text-xs font-mediu py-2 border rounded-lg tracking-wide">{t('upload photo')}
                                                        <input id="dropzone-file" type="file" value="" className="hidden" onChange={handleImageChange}  />
                                                    </label>
                                                </div>
                                            : null
                                        }
                                        { showOpenCamera ? <CameraCaptureComponent sendCameraCloseDataFromChild={handleCameraCloseDataFromChild} sendImageDataFromChild={handleImageDataFromChild} /> : null }
                                        {error && <p className="text-red-normal text-xs font-semibold">{error}</p>}
                                    </div>
                                </div>
                                <div className="bg-white border border-white rounded-lg py-2 sm:py-4">
                                    <div className="px-2 sm:px-4 flex items-center justify-between">
                                        <h1 className="text-base sm:text-lg font-semibold capitalize">{ t('business details')}</h1>
                                    </div>
                                    <div className="my-4 border-t border-background-normal"></div>
                                    <div className="px-2 sm:px-4 sm:grid sm:grid-cols-2 sm:gap-4 text-sm sm:text-sm lg:text-base space-y-2 sm:space-y-0">
                                        <div className="space-y-2 relative">
                                            <div className="font-semibold capitalize">
                                                { t('category')}
                                            </div>
                                            <button type="button" onClick={(e) => handleShowCategory(e)} className="flex justify-between items-center border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full">
                                                <span className={(categoryData.categoryName ? "text-black" : "text-gray-dark") + " capitalize mr-1 w-full text-left"} >{categoryData.categoryName ? categoryData.categoryName : t('select')}</span>
                                                <img src="../../../assets/icons/select-down.svg" alt="select-down" />
                                            </button>
                                            {
                                                showCategory
                                                ? 
                                                    <ul className="absolute w-full z-30 text-background-dark bg-white shadow-md rounded-md top-16 sm:top-[4.5rem] right-0 max-h-28 overflow-y-auto no-scrollbar">
                                                        {categoryList.map((category, index) => (
                                                            <li key={`category`+index} onClick={(e) => handleCategory((e), category)}  className="bg-white hover:bg-action-hover py-2 px-2 block whitespace-no-wrap cursor-pointer capitalize">{category.categoriesname}</li>
                                                        ))}
                                                    </ul>                            
                                                : null
                                            }
                                        </div>
                                        <div className="space-y-2 relative">
                                            <div className="font-semibold capitalize">
                                                { t('business unit')}
                                            </div>
                                            <button type="button" onClick={(e) => handleShowBusinessUnit(e)} className="flex justify-between items-center border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full">
                                                <span className={(businessData.businessName ? "text-black" : "text-gray-dark") + " capitalize mr-1 w-full text-left"}>{businessData.businessName ? businessData.businessName : t('select')}</span>
                                                <img src="../../../assets/icons/select-down.svg" alt="select-down" />
                                            </button>
                                            {
                                                showBusinessUnit
                                                ? 
                                                    <ul className="absolute w-full z-30 text-background-dark bg-white shadow-md rounded-md top-16 sm:top-[4.5rem] right-0 max-h-28 overflow-y-auto no-scrollbar">
                                                        {businessUnitList.map((business, index) => (
                                                            <li key={`business`+index} onClick={(e) => handleBusinessUnit((e), business)}  className="bg-white hover:bg-action-hover py-2 px-2 block whitespace-no-wrap cursor-pointer capitalize">{business.name}</li>
                                                        ))}
                                                    </ul>                            
                                                : null
                                            }
                                        </div>
                                        <div className="space-y-2 relative">
                                            <div className="font-semibold capitalize">
                                                { t('customer partners')}
                                            </div>

                                            <button type="button" onClick={(e) => handleShowPartner(e)} className="flex justify-between items-center border-background-normal focus:outline-none bg-white border-2 rounded-lg px-4 py-2 w-full">
                                                <span className={((selectedPartners.length > 0) ? "text-black" : "text-gray-dark") + " capitalize mr-1 w-full text-left"}>{(selectedPartners.length > 0) ? (partnerList.filter((partner) => selectedPartners.includes(partner.PartnerId)).map((partner) => partner.Name).join(", ")) : t('select partner')}</span>
                                                <img src="../../../assets/icons/select-down.svg" alt="select-down" />
                                            </button>
                                            {
                                                showPartner
                                                ? 
                                                    <ul className="absolute w-full z-30 text-background-dark bg-white shadow-md rounded-md top-16 sm:top-[4.5rem] right-0 max-h-28 overflow-y-auto no-scrollbar">
                                                        {partnerList.map((partner) => (
                                                            <li key={partner.PartnerId} className={`flex items-center p-3 hover:bg-gray-100`}>
                                                                <label className="relative flex items-center cursor-pointer">
                                                                    <input type="checkbox" checked={selectedPartners.includes(partner.PartnerId)} onChange={() => handlePartnerChange(partner.PartnerId)} className="before:content[''] peer relative h-6 w-6 cursor-pointer appearance-none rounded border border-gray-dark transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-white before:opacity-0 before:transition-opacity checked:border-blue-normal checked:bg-blue-normal checked:before:bg-blue-normal" />
                                                                    <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" stroke="currentColor" strokeWidth="1">
                                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                                                        </svg>
                                                                    </span>
                                                                </label>
                                                                <div className="font-normal text-xs sm:text-base ml-2 text-light-400">{partner.Name}</div>
                                                                <label className={(selectedPartners.includes(partner.PartnerId) ? "cursor-pointer" : "cursor-not-allowed") + " ml-auto relative flex items-center"} htmlFor={partner.PartnerId}>
                                                                    <input type="radio" id={partner.PartnerId} name="defaultPartner" checked={defaultPartner === partner.PartnerId} onChange={() => handleDefaultChange(partner.PartnerId)} disabled={!selectedPartners.includes(partner.PartnerId)} className="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-400 checked:border-blue-normal bg-white transition-all" />
                                                                    <span className="absolute bg-blue-normal w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                                                                </label>
                                                            </li>
                                                        ))}
                                                    </ul>                            
                                                : null
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className={(showBusinessUnit || showCategory || showPartner) ? "pb-28" : "sm:py-2 py-7"}></div>
                            </div>
                        }
                    </div>
                </form>

                {
                    showMap ?
                    <div className="overflow-y-auto overflow-x-hidden bg-blue-300 bg-opacity-50 fixed flex flex-col top-0 right-0 left-0 z-50 justify-center items-center w-full inset-0 max-h-full">
                        <div className="relative p-4 w-full max-w-5xl max-h-full bg-white rounded-lg">
                            <button onClick={(e) => setShowMap(!showMap)} className="absolute right-1 top-1 border border-gray-400 shadow p-3 bg-white z-20 text-gray-400 rounded-full" >
                                <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                                </svg>    
                            </button>
                            <MapComponent markers={markers} draggable={false} radius={0}/>
                        </div>
                    </div>
                    : null
                }
            </Layout>
            {/* page */}
            <div className="fixed bottom-0 sm:hidden flex items-center bg-white rounded-t-xl border border-background-normal justify-between p-3 shadow-md w-screen">
                {!formButton &&
                    <NavLink to={`/profiles/detail/${customerid}`}>
                        <button type="button" className="text-center px-2 sm:px-4 text-sm font-medium sm:text-base py-2 text-blue-normal bg-white border border-blue-normal rounded-lg tracking-wide capitalize">{ t('cancel') }</button>
                    </NavLink>
                }
                <button type={formButton ? "button" : "submit"} className="text-center px-2 sm:px-4 text-sm font-medium sm:text-base py-2 text-white bg-blue-normal border border-blue-normal rounded-lg tracking-wide capitalize">{formButton ? t('Please wait, in-progress') : t('save') }</button>
            </div>
            {/* common */}
            {showWarningPopUp && <WarningPopUp customerId={customerid} visitTypeData={visitTypeData} sendWarningPopUpDataFromChild={handleWarningPopUpDataFromChild} /> }
        </div>
    )
}

export default ProfileEdit;